<template>
  <div>
    <t-loading v-model="loading" />
    <v-row class="signin">
      <v-col class="card-signin">
        <v-img :src="logo" max-width="150" contain class="center-img" />
        <h2 class="white--text text-uppercase">{{ getTitle }}</h2>
        <v-alert v-if="showAlert" :type="typeAlert" dense>{{ messageAlert }}</v-alert>
        <v-form ref="form" @submit.prevent="login" v-if="!newPasswordRequired && !mfaRequired">
          <v-text-field
            v-model="email"
            :label="$t('email')"
            outlined
            dark
            :rules="[rules.required, rules.email]"
            filled
            dense
          ></v-text-field>
          <v-text-field
            v-model="password"
            :label="$t('password')"
            :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPass = !showPass"
            outlined
            :rules="[rules.required]"
            dense
            dark
            filled
            :type="showPass ? 'text' : 'password'"
          ></v-text-field>
          <v-row class="mb-0 text-right">
            <v-col class="py-1">
              <a href="" @click.prevent="forgotPassword" class="white--text pb-2">{{ $t('forgotPassword') }}</a>
            </v-col>
          </v-row>
          <v-btn type="submit" color="secondary white--text" dark width="100%">
            {{ $t('signIn') }}
          </v-btn>
        </v-form>
        <v-form ref="formNewPassword" @submit.prevent="loginNewPassword" v-if="newPasswordRequired">
          <v-text-field
            v-model="password"
            :label="$t('newPassword')"
            outlined
            :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPass = !showPass"
            :type="showPass ? 'text' : 'password'"
            dark
            :rules="[rules.required, rules.lengthPassword]"
            filled
            dense
          ></v-text-field>
          <v-text-field
            v-model="confirmNewPassword"
            :label="$t('confirmNewPassword')"
            :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPass = !showPass"
            outlined
            :rules="[rules.equalPasswords, rules.lengthPassword]"
            dense
            dark
            filled
            :type="showPass ? 'text' : 'password'"
          ></v-text-field>
          <v-btn type="submit" color="secondary white--text" dark width="100%">
            {{ $t('signIn') }}
          </v-btn>
        </v-form>
        <v-form ref="formMfa" @submit.prevent="confirmMfaCode" v-if="mfaRequired">
          <p class="white--text text-body-2 mb-4">{{ $t('mfaCodeSentTo', { email: maskedEmail }) }}</p>
          <v-text-field
            v-model="mfaCode"
            :label="$t('mfaCode')"
            outlined
            dark
            inputmode="numeric"
            autocomplete="one-time-code"
            :rules="[rules.required]"
            filled
            dense
          ></v-text-field>
          <v-btn type="submit" color="secondary white--text" dark width="100%">
            {{ $t('confirm') }}
          </v-btn>
          <v-row class="mt-2 text-center">
            <v-col class="py-1">
              <span v-if="resendCooldown > 0" class="white--text text-body-2">
                {{ $t('resendCodeIn', { seconds: resendCooldown }) }}
              </span>
              <a v-else href="" @click.prevent="resendMfaCode" class="white--text">{{ $t('resendCode') }}</a>
            </v-col>
          </v-row>
          <v-row class="text-center">
            <v-col class="py-1">
              <a href="" @click.prevent="cancelMfa" class="white--text">{{ $t('backToLogin') }}</a>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import logo from '../assets/img/logo.png'
import { Auth } from 'aws-amplify'
import mixinRouter from '@/mixins/mixinRouter'
import mixinErrorCognito from '@/mixins/mixinErrorCognito'
import mixinLoginRules from '@/mixins/mixinLoginRules'

// O reenvio trafega como uma resposta ao desafio; o que o identifica para os
// triggers é o clientMetadata, não a resposta em si (ignorada no back-end).
const RESEND_ANSWER = '__resend__'
const RESEND_METADATA = { action: 'RESEND' }
const RESEND_COOLDOWN_SECONDS = 60

export default {
  mixins: [mixinRouter, mixinErrorCognito, mixinLoginRules],
  data() {
    return {
      user: null,
      messageAlert: '',
      typeAlert: '',
      email: '',
      newPasswordRequired: '',
      showPass: false,
      loading: false,
      logo: logo,
      mfaRequired: false,
      mfaCode: '',
      maskedEmail: '',
      resendCooldown: 0,
      resendInterval: null,
    }
  },
  beforeDestroy() {
    this.stopResendCooldown()
  },
  computed: {
    getTitle() {
      if (this.mfaRequired) return this.$i18n.t('mfaTitle')
      return this.newPasswordRequired ? this.$i18n.t('newPassword') : this.$i18n.t('login')
    },
    showAlert() {
      return this.typeAlert !== ''
    },
  },
  methods: {
    async loginNewPassword() {
      this.clearAlert()
      const valid = await this.$refs.formNewPassword.validate()
      if (!valid) return
      this.loading = true
      try {
        const { challengeParam } = this.user
        const { userAttributes } = challengeParam
        this.user = await Auth.completeNewPassword(this.user, this.confirmNewPassword, {
          name: userAttributes.name || userAttributes.email,
          profile: userAttributes.profile || 'admin',
        })
        if (this.handleChallenge()) {
          this.newPasswordRequired = false
          this.loading = false
          return
        }
        this.redirectHome()
      } catch (error) {
        console.error(error)
        this.setError(error?.code)
      }
      this.loading = false
    },
    async login() {
      this.clearAlert()
      const valid = await this.$refs.form.validate()
      if (!valid) return
      this.loading = true
      try {
        this.user = await Auth.signIn(this.email, this.password)
        this.newPasswordRequired = this.user?.challengeName === 'NEW_PASSWORD_REQUIRED'
        if (this.newPasswordRequired) {
          this.resetFormNewPassword()
          this.loading = false
          return
        }
        if (this.handleChallenge()) {
          this.loading = false
          return
        }
        this.redirectHome()
      } catch (error) {
        console.error(error)
        this.setError(error?.code)
      }
      this.loading = false
    },
    handleChallenge() {
      if (this.user?.challengeName !== 'CUSTOM_CHALLENGE') return false
      this.mfaRequired = true
      this.mfaCode = ''
      this.maskedEmail = this.user?.challengeParam?.email || ''
      this.startResendCooldown()
      return true
    },
    startResendCooldown() {
      this.stopResendCooldown()
      this.resendCooldown = RESEND_COOLDOWN_SECONDS
      this.resendInterval = setInterval(() => {
        this.resendCooldown -= 1
        if (this.resendCooldown <= 0) this.stopResendCooldown()
      }, 1000)
    },
    stopResendCooldown() {
      if (this.resendInterval) clearInterval(this.resendInterval)
      this.resendInterval = null
      this.resendCooldown = 0
    },
    async resendMfaCode() {
      this.clearAlert()
      this.loading = true
      try {
        this.user = await Auth.sendCustomChallengeAnswer(this.user, RESEND_ANSWER, RESEND_METADATA)
        // Reenvio bem-sucedido devolve um novo CUSTOM_CHALLENGE; estourar o
        // limite derruba a sessão e cai no catch como NotAuthorizedException.
        this.maskedEmail = this.user?.challengeParam?.email || this.maskedEmail
        this.mfaCode = ''
        this.startResendCooldown()
        this.typeAlert = 'success'
        this.messageAlert = this.$i18n.t('mfaCodeResent')
      } catch (error) {
        console.error(error)
        this.resetToLogin()
        this.setError('MfaResendLimitExceeded')
      }
      this.loading = false
    },
    async confirmMfaCode() {
      this.clearAlert()
      const valid = await this.$refs.formMfa.validate()
      if (!valid) return
      this.loading = true
      try {
        this.user = await Auth.sendCustomChallengeAnswer(this.user, this.mfaCode.trim())
        // Amplify NÃO rejeita em código errado: devolve novo CUSTOM_CHALLENGE.
        // Só há sessão quando o Cognito emitiu tokens.
        await Auth.currentAuthenticatedUser()
        this.redirectHome()
      } catch (error) {
        console.error(error)
        if (error?.code === 'NotAuthorizedException') {
          // tentativas esgotadas derrubam a sessão
          this.resetToLogin()
          this.setError('MfaAttemptsExceeded')
        } else {
          this.mfaCode = ''
          this.setError('CodeMismatchException')
        }
      }
      this.loading = false
    },
    cancelMfa() {
      this.clearAlert()
      this.resetToLogin()
    },
    resetToLogin() {
      this.stopResendCooldown()
      this.mfaRequired = false
      this.mfaCode = ''
      this.maskedEmail = ''
      this.user = null
      this.password = ''
    },
    setError(code) {
      this.typeAlert = 'error'
      this.messageAlert = this.getMessageError(code)
    },
    resetFormNewPassword() {
      this.password = ''
      this.$nextTick(function () {
        this.$refs.formNewPassword.reset()
      })
    },
    clearAlert() {
      this.typeAlert = ''
      this.messageAlert = ''
    },
    clear() {
      this.email = ''
      this.user = null
      this.password = ''
      this.confirmNewPassword = ''
      this.newPasswordRequired = false
      this.mfaRequired = false
      this.mfaCode = ''
      this.maskedEmail = ''
    },
    redirectHome() {
      this.redirect({
        path: '/',
      })
    },
    forgotPassword() {
      this.redirect({
        path: '/forgot-password',
      })
    },
  },
}
</script>
